// 2026-07-25:Media preview modal — 按 mime_type 分流 image / video / audio / text / 其他。
//
// 设计要点:
//   - 不新增后端 API,完全前端 fetch(source_url)
//   - 打开时禁止背景滚动,关闭恢复(useEffect + document.body.style.overflow)
//   - Esc 关闭、点遮罩关闭、内容区 click stopPropagation
//   - 资源清理:video/audio.pause(),AbortController 取消未完成 text fetch
//   - 层级 z-index 300(高于 header / sidebar / 普通 modal)
//   - text 预览限制 1MiB、仅 UTF-8,失败显示 "ダウンロードしてご確認ください" + 2 个按钮

import { useEffect, useRef, useState } from "react";
import { X, Download, ExternalLink, FileText, AlertCircle } from "lucide-react";
import type { MediaItem } from "../api/mediaApi";

const TEXT_MAX_BYTES = 1024 * 1024; // 1 MiB

interface MediaPreviewModalProps {
  item: MediaItem | null;
  onClose: () => void;
}

export function MediaPreviewModal({ item, onClose }: MediaPreviewModalProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // 2026-07-25 简化:下载按钮直接 window.open(source_url),浏览器按 content-type 处理。
  // 不再做 fetch + Blob,也不依赖任何后端 API。
  const handleDownload = (
    event: React.MouseEvent | React.SyntheticEvent,
    media: MediaItem,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (!media.source_url) return;
    window.open(media.source_url, "_blank", "noopener,noreferrer");
  };

  // text preview state
  const [textState, setTextState] = useState<
    | { status: "loading" }
    | { status: "ready"; content: string }
    | { status: "too_large" }
    | { status: "error" }
  >({ status: "loading" });

  // Modal 打开时锁滚动,关闭时恢复 + 清理资源
  useEffect(() => {
    if (!item) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
      // 资源清理
      videoRef.current?.pause();
      audioRef.current?.pause();
      abortRef.current?.abort();
    };
  }, [item]);

  // Esc 关闭
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  // text fetch — 仅当 mime_type 以 text/ 开头
  useEffect(() => {
    if (!item || !item.source_url) return;
    const mime = (item.mime_type || "").toLowerCase();
    if (!mime.startsWith("text/")) {
      setTextState({ status: "ready", content: "" });
      return;
    }
    setTextState({ status: "loading" });
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    (async () => {
      try {
        const resp = await fetch(item.source_url!, { signal: controller.signal });
        if (!resp.ok) {
          setTextState({ status: "error" });
          return;
        }
        // 先取 Content-Length(若有),超过 1MiB 直接放弃
        const lenHeader = resp.headers.get("content-length");
        if (lenHeader && Number(lenHeader) > TEXT_MAX_BYTES) {
          setTextState({ status: "too_large" });
          return;
        }
        const blob = await resp.blob();
        if (blob.size > TEXT_MAX_BYTES) {
          setTextState({ status: "too_large" });
          return;
        }
        const buf = await blob.arrayBuffer();
        const text = new TextDecoder("utf-8").decode(buf);
        setTextState({ status: "ready", content: text });
      } catch (e) {
        if (controller.signal.aborted) return; // 关闭时主动取消
        setTextState({ status: "error" });
      }
    })();

    return () => controller.abort();
  }, [item]);

  if (!item) return null;

  const mime = (item.mime_type || "").toLowerCase();
  const isImage = mime.startsWith("image/");
  const isVideo = mime.startsWith("video/");
  const isAudio = mime.startsWith("audio/");
  const isText = mime.startsWith("text/");

  // ---- 渲染内容 ----
  const renderBody = () => {
    if (!item.source_url) {
      return <NotPreviewable item={item} reason="ファイルの URL が取得できませんでした。" onDownload={handleDownload} />;
    }
    if (isImage) {
      return (
        <div className="media-preview-body media-preview-body--image" data-testid="media-preview-image">
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img
            src={item.source_url}
            alt={item.filename || item.title || ""}
            draggable={false}
            className="media-preview-image"
          />
        </div>
      );
    }
    if (isVideo) {
      return (
        <div className="media-preview-body media-preview-body--video" data-testid="media-preview-video">
          <video
            ref={videoRef}
            src={item.source_url}
            controls
            playsInline
            className="media-preview-video"
          />
        </div>
      );
    }
    if (isAudio) {
      return (
        <div className="media-preview-body media-preview-body--audio" data-testid="media-preview-audio">
          <audio ref={audioRef} src={item.source_url} controls className="media-preview-audio" />
        </div>
      );
    }
    if (isText) {
      if (textState.status === "loading") {
        return (
          <div className="media-preview-loading">
            <FileText size={32} color="#94a3b8" />
            <span>読み込み中...</span>
          </div>
        );
      }
      if (textState.status === "too_large") {
        return (
          <NotPreviewable
            item={item}
            reason={`ファイルサイズが ${TEXT_MAX_BYTES / 1024 / 1024} MiB を超えているため、プレビューできません。`}
            onDownload={handleDownload}
          />
        );
      }
      if (textState.status === "error") {
        return (
          <NotPreviewable
            item={item}
            reason="このファイルはオンラインプレビューできません。ダウンロードしてご確認ください。"
            onDownload={handleDownload}
          />
        );
      }
      return (
        <div className="media-preview-body media-preview-body--text" data-testid="media-preview-text">
          <pre className="media-preview-text-pre">
            <code>{textState.content}</code>
          </pre>
        </div>
      );
    }
    return (
      <NotPreviewable
        item={item}
        reason="このファイル形式はブラウザでプレビューできません。ダウンロードしてご確認ください。"
        onDownload={handleDownload}
      />
    );
  };

  const name = item.filename || item.title || `#${item.id}`;

  return (
    <div
      className="media-preview-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`プレビュー: ${name}`}
      data-testid="media-preview-modal"
    >
      <div className="media-preview-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <header className="media-preview-header">
          <div className="media-preview-meta">
            <div className="media-preview-name" title={name}>
              {name}
            </div>
            <div className="media-preview-info">
              <span>{item.mime_type || "unknown"}</span>
              <span>·</span>
              <span>{formatSize(item.file_size)}</span>
              <span>·</span>
              <span>{formatDate(item.created_at)}</span>
            </div>
          </div>
          <div className="media-preview-toolbar">
            <a
              href={item.source_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="media-preview-btn"
              title="原ファイルを開く"
              aria-label="原ファイルを開く"
              data-testid="media-preview-open-original"
              onClick={(e) => {
                if (!item.source_url) e.preventDefault();
              }}
            >
              <ExternalLink size={14} />
            </a>
            <button
              type="button"
              className="media-preview-btn media-preview-btn--primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownload(e, item);
              }}
              disabled={!item.source_url}
              title="ダウンロード"
              aria-label="ダウンロード"
              data-testid="media-preview-download"
            >
              <Download size={14} />
              <span>ダウンロード</span>
            </button>
            <button
              type="button"
              className="media-preview-btn media-preview-btn--close"
              onClick={onClose}
              title="閉じる"
              aria-label="閉じる"
              data-testid="media-preview-close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        {/* Body */}
        {renderBody()}
      </div>
    </div>
  );
}

// ---- NotPreviewable fallback ----

function NotPreviewable({
  item,
  reason,
  onDownload,
}: {
  item: MediaItem;
  reason: string;
  onDownload: (e: React.MouseEvent | React.SyntheticEvent, item: MediaItem) => void | Promise<void>;
}) {
  return (
    <div className="media-preview-body media-preview-body--fallback" data-testid="media-preview-unsupported">
      <div className="media-preview-fallback">
        <AlertCircle size={36} color="#94a3b8" />
        <div className="media-preview-fallback-reason">{reason}</div>
        <div className="media-preview-fallback-actions">
          <button
            type="button"
            className="media-state-cta"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDownload(e, item);
            }}
            disabled={!item.source_url}
            data-testid="media-preview-fallback-download"
          >
            <Download size={14} />
            <span>ダウンロード</span>
          </button>
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="media-state-cta"
              style={{ background: "#475569" }}
              data-testid="media-preview-fallback-open"
            >
              <ExternalLink size={14} />
              <span>原ファイルを開く</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- formatters (复用 MediaPage 的,这里复制一份避免跨文件导入) ----

function formatSize(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return m ? m[1] : iso;
}